import { useState, useCallback, useRef, useEffect } from 'react';
import { Outfit } from '../types';
import { NarratorVoice } from '../types/fashion-show';
import FashionShowService from '../services/fashion-show-service';

export interface UseNarrationReturn {
  isMuted: boolean;
  isPlaying: boolean;
  isFetching: boolean;
  toggleMute: () => void;
  playForOutfit: (outfit: Outfit) => Promise<void>;
  prefetchForOutfit: (outfit: Outfit) => void;
  pause: () => void;
  resume: () => void;
  stop: () => void;
}

/**
 * Custom hook for managing AI voice narration during fashion show reel playback.
 *
 * Calls FashionShowService.generateNarration() to get base64-encoded audio,
 * decodes it to a blob URL, and plays via HTMLAudioElement. Maintains a
 * pre-fetch cache (Map<string, string>) keyed by outfitId for seamless
 * transitions. Supports pause/resume preserving audio currentTime, stop,
 * and an independent mute toggle. Fires onNarrationStart/onNarrationEnd
 * callbacks for volume ducking coordination with background music.
 *
 * Gracefully handles narration failures — logs the error and continues
 * without narration for that outfit. Cleans up blob URLs and audio element
 * on unmount.
 *
 * Callback split for synchronization:
 * - onNarrationFetchStart: called IMMEDIATELY when playForOutfit begins (before async fetch).
 *   Used to hold outfit advancement so the timer can't fire during the API call.
 * - onAudioPlaying: called when audio actually starts playing. Used for volume ducking.
 * - onNarrationEnd: called when audio finishes, errors, or fetch fails. Releases the hold.
 *
 * Requirements: 10.1, 10.4, 10.5, 10.6, 10.7, 10.8, 10.9
 */
export function useNarration(
  onNarrationFetchStart: () => void,
  onNarrationEnd: () => void,
  voice: NarratorVoice = 'male',
  narrationRate: number = 1.0,
  onAudioPlaying?: () => void,
): UseNarrationReturn {
  const [isMuted, setIsMuted] = useState<boolean>(false);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [isFetching, setIsFetching] = useState<boolean>(false);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const mountedRef = useRef<boolean>(true);
  const isMutedRef = useRef<boolean>(false);
  const cacheRef = useRef<Map<string, string>>(new Map());
  const pendingFetchRef = useRef<Map<string, Promise<string | null>>>(new Map());
  const onNarrationFetchStartRef = useRef(onNarrationFetchStart);
  const onNarrationEndRef = useRef(onNarrationEnd);
  const onAudioPlayingRef = useRef(onAudioPlaying);
  // Track the current outfit being narrated to prevent stale async completions
  const currentOutfitIdRef = useRef<string | null>(null);
  // Guards against spurious audio 'ended'/'error' events releasing the hold.
  // Only true when audio.play() has been called and we're waiting for it to finish.
  // This prevents the hold from being released during the fetch phase if the
  // audio element fires an error event (e.g., from an empty src or browser quirk).
  const audioActiveRef = useRef<boolean>(false);

  // Keep callback refs up to date
  useEffect(() => {
    onNarrationFetchStartRef.current = onNarrationFetchStart;
    onNarrationEndRef.current = onNarrationEnd;
    onAudioPlayingRef.current = onAudioPlaying;
  }, [onNarrationFetchStart, onNarrationEnd, onAudioPlaying]);

  // Keep muted ref in sync with state
  useEffect(() => {
    isMutedRef.current = isMuted;
  }, [isMuted]);

  // Create audio element on mount, clean up on unmount
  useEffect(() => {
    mountedRef.current = true;
    const audio = new Audio();
    audio.preload = 'auto';
    audioRef.current = audio;

    const handleEnded = () => {
      if (mountedRef.current && audioActiveRef.current) {
        audioActiveRef.current = false;
        setIsPlaying(false);
        console.log('[useNarration] Audio ended, releasing hold');
        onNarrationEndRef.current();
      }
    };

    const handleError = () => {
      if (mountedRef.current && audioActiveRef.current) {
        audioActiveRef.current = false;
        setIsPlaying(false);
        console.log('[useNarration] Audio error, releasing hold');
        onNarrationEndRef.current();
      } else if (mountedRef.current) {
        // Spurious error event (e.g., empty src on mount) — ignore it
        console.log('[useNarration] Ignoring spurious audio error event (not in active narration)');
      }
    };

    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);

    return () => {
      mountedRef.current = false;

      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);

      // Stop and clean up audio element
      audio.pause();
      audio.src = '';
      audio.load();
      audioRef.current = null;

      // Revoke all cached blob URLs
      cacheRef.current.forEach((blobUrl) => {
        URL.revokeObjectURL(blobUrl);
      });
      cacheRef.current.clear();
      pendingFetchRef.current.clear();
    };
  }, []);

  /**
   * Convert base64 audio data to a blob URL for playback.
   * Handles various content types returned by the backend (audio/mpeg, audio/lpcm, etc.)
   */
  const base64ToBlobUrl = useCallback((base64Data: string, contentType: string): string => {
    // Default to audio/mpeg if content type is not standard
    const mimeType = contentType === 'audio/lpcm' ? 'audio/wav' : (contentType || 'audio/mpeg');

    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: mimeType });
    return URL.createObjectURL(blob);
  }, []);

  /**
   * Fetch narration audio for an outfit and return the blob URL.
   * Returns null if the fetch fails (graceful degradation).
   */
  const fetchNarration = useCallback(async (outfit: Outfit): Promise<string | null> => {
    // Check cache first
    const cached = cacheRef.current.get(outfit.id);
    if (cached) return cached;

    // Check if there's already a pending fetch for this outfit
    const pending = pendingFetchRef.current.get(outfit.id);
    if (pending) return pending;

    // Create the fetch promise
    const fetchPromise = (async (): Promise<string | null> => {
      try {
        const response = await FashionShowService.generateNarration(outfit, voice);

        if (!mountedRef.current) return null;

        if (!response.audioBase64) {
          console.warn(`[useNarration] No audio data returned for outfit ${outfit.id}`);
          return null;
        }

        const blobUrl = base64ToBlobUrl(response.audioBase64, response.contentType);

        // Store in cache
        cacheRef.current.set(outfit.id, blobUrl);
        return blobUrl;
      } catch (error) {
        // Graceful degradation: log error, continue without narration
        console.error(`[useNarration] Failed to fetch narration for outfit ${outfit.id}:`, error);
        return null;
      } finally {
        // Remove from pending fetches
        pendingFetchRef.current.delete(outfit.id);
      }
    })();

    // Store as pending
    pendingFetchRef.current.set(outfit.id, fetchPromise);
    return fetchPromise;
  }, [base64ToBlobUrl]);

  /**
   * Pre-fetch narration audio for an outfit (fire-and-forget).
   * Called for the next outfit while the current one is displaying.
   * Requirements: 10.7
   */
  const prefetchForOutfit = useCallback((outfit: Outfit) => {
    // Fire and forget — just populate the cache
    fetchNarration(outfit).catch(() => {
      // Already handled inside fetchNarration
    });
  }, [fetchNarration]);

  /**
   * Play narration audio for the given outfit.
   * Stops any currently playing narration first.
   *
   * CRITICAL SYNC: Calls onNarrationFetchStart IMMEDIATELY (synchronously)
   * before the async fetch begins. This holds outfit advancement so the
   * timer cannot fire while we're waiting for Bedrock/Polly. The hold is
   * only released when audio finishes playing, errors, or fetch fails.
   *
   * If muted, still fetches but doesn't play — releases hold after fetch.
   * Requirements: 10.1, 10.8
   */
  const playForOutfit = useCallback(async (outfit: Outfit) => {
    const audio = audioRef.current;
    if (!audio || !mountedRef.current) return;

    // Track which outfit we're narrating to detect stale completions
    currentOutfitIdRef.current = outfit.id;

    // Stop any currently playing narration (but don't release hold —
    // we're about to re-hold for the new outfit)
    if (!audio.paused) {
      audio.pause();
      audio.currentTime = 0;
      audioActiveRef.current = false;
      setIsPlaying(false);
      // Don't call onNarrationEnd here — we're transitioning to a new outfit's
      // narration, so the hold should stay active. The old narration's "end"
      // is effectively replaced by the new narration's "start".
    }

    // IMMEDIATELY hold advancement before the async fetch begins.
    // This is the critical fix: the hold must be established synchronously
    // so the interval timer cannot fire during the API call to Bedrock/Polly.
    console.log(`[useNarration] Holding advance for outfit ${outfit.id} — starting fetch`);
    setIsFetching(true);
    onNarrationFetchStartRef.current();

    // Fetch the narration audio (may come from cache)
    const blobUrl = await fetchNarration(outfit);

    // Check if this outfit is still the current one (user may have exited or skipped)
    if (!mountedRef.current || currentOutfitIdRef.current !== outfit.id) {
      console.log(`[useNarration] Outfit ${outfit.id} is stale, skipping playback`);
      setIsFetching(false);
      return;
    }

    if (!blobUrl) {
      // Fetch failed — release the hold so the reel can advance
      console.warn(`[useNarration] No audio for outfit ${outfit.id}, releasing hold`);
      setIsFetching(false);
      onNarrationEndRef.current();
      return;
    }

    // Set the audio source and playback rate
    audio.src = blobUrl;
    audio.currentTime = 0;
    audio.playbackRate = narrationRate;
    setIsFetching(false);

    // If muted, don't play but release the hold so reel can advance
    if (isMutedRef.current) {
      console.log(`[useNarration] Narration muted for outfit ${outfit.id}, releasing hold`);
      onNarrationEndRef.current();
      return;
    }

    try {
      // Fire audio playing callback (triggers music volume duck)
      if (onAudioPlayingRef.current) {
        onAudioPlayingRef.current();
      }
      // Mark audio as active BEFORE calling play() — the 'ended' and 'error'
      // event handlers will only release the hold when this flag is true.
      audioActiveRef.current = true;
      setIsPlaying(true);

      await audio.play();
      // Note: the hold is released when the 'ended' event fires on the audio element,
      // which calls onNarrationEndRef.current(). This ensures the outfit stays visible
      // for the entire duration of the narration.
    } catch (error) {
      // Playback failed — release the hold so reel can advance
      console.warn(`[useNarration] Playback failed for outfit ${outfit.id}:`, error);
      if (mountedRef.current) {
        audioActiveRef.current = false;
        setIsPlaying(false);
        onNarrationEndRef.current();
      }
    }
  }, [fetchNarration]);

  /**
   * Pause narration audio, preserving the current playback position.
   * Requirements: 10.4
   */
  const pause = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || audio.paused) return;
    audio.pause();
    setIsPlaying(false);
  }, []);

  /**
   * Resume narration audio from where it was paused.
   * Requirements: 10.5
   */
  const resume = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || !audio.src || isMutedRef.current) return;

    // Only resume if there's remaining audio to play
    if (audio.currentTime > 0 && audio.currentTime < audio.duration) {
      // Re-hold advance since narration is resuming
      onNarrationFetchStartRef.current();
      if (onAudioPlayingRef.current) {
        onAudioPlayingRef.current();
      }
      audioActiveRef.current = true;
      setIsPlaying(true);

      const playPromise = audio.play();
      if (playPromise !== undefined) {
        playPromise.catch((err: Error) => {
          console.warn('[useNarration] Resume failed:', err.message);
          if (mountedRef.current) {
            audioActiveRef.current = false;
            setIsPlaying(false);
            onNarrationEndRef.current();
          }
        });
      }
    }
  }, []);

  /**
   * Stop narration audio entirely and reset playback position.
   * Requirements: 10.6
   */
  const stop = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
    audio.currentTime = 0;
    currentOutfitIdRef.current = null;
    const wasActive = audioActiveRef.current;
    audioActiveRef.current = false;
    if (isPlaying || wasActive) {
      onNarrationEndRef.current();
    }
    setIsPlaying(false);
    setIsFetching(false);
  }, [isPlaying]);

  /**
   * Toggle narration mute independently of music mute.
   * When muting, pause any playing narration.
   * When unmuting, narration will play on the next outfit.
   * Requirements: 10.9
   */
  const toggleMute = useCallback(() => {
    setIsMuted((prev) => {
      const newMuted = !prev;
      isMutedRef.current = newMuted;

      if (newMuted) {
        // Muting: stop any playing narration
        const audio = audioRef.current;
        if (audio && !audio.paused) {
          audio.pause();
          audio.currentTime = 0;
          audioActiveRef.current = false;
          setIsPlaying(false);
          onNarrationEndRef.current();
        }
      }

      return newMuted;
    });
  }, []);

  return {
    isMuted,
    isPlaying,
    isFetching,
    toggleMute,
    playForOutfit,
    prefetchForOutfit,
    pause,
    resume,
    stop,
  };
}
